import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { adminLogout } from '../api/auth';
import {
  getAdminProducts, createProduct, updateProduct, deleteProduct,
  getAdminReels,    createReel,    updateReel,    deleteReel,
  getAdminCollections, createCollection, updateCollection, deleteCollection,
  uploadImage,
  getAdminOrders, updateOrderStatus,
  getAdminBanners, createBanner, updateBanner, deleteBanner,
} from '../api/admin';

const BADGE_OPTIONS = ['New', 'Sale', 'Trending', 'Bestseller', 'Limited', 'Hot', 'Exclusive'];

const EMPTY_PRODUCT = {
  name: '', description: '', offer_text: '',
  original_price: '', sale_price: '',
  badge: '', reel_id: '',
  images: [],
  colors: [], sizes: [],
  is_featured: false, is_active: true,
  product_code: '',
  stock_qty: 0,
};

const EMPTY_REEL = {
  title: '', video_url: '',
  handle: '@aamira jewellery', is_active: true,
};

const EMPTY_COLLECTION = {
  name: '', slug: '', sub: '', badge: '',
  image_url: '', is_featured: false, is_luxe: false, sort_order: 0,
};

const Field = ({ label, required, children }) => (
  <div className="adm-field">
    <label className="adm-label">{label}{required && <span className="adm-req">*</span>}</label>
    {children}
  </div>
);
const Input    = (props) => <input className="adm-input" {...props} />;
const Textarea = (props) => <textarea className="adm-textarea" rows={3} {...props} />;
const Select   = ({ children, ...props }) => <select className="adm-input" {...props}>{children}</select>;

const TagInput = ({ label, values, onChange, placeholder }) => {
  const [val, setVal] = useState('');
  const add = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && val.trim()) {
      e.preventDefault();
      if (!values.includes(val.trim())) onChange([...values, val.trim()]);
      setVal('');
    }
  };
  return (
    <Field label={label}>
      <div className="adm-tag-wrap">
        {values.map(v => (
          <span key={v} className="adm-tag">
            {v}
            <button type="button" onClick={() => onChange(values.filter(x => x !== v))} className="adm-tag-x">×</button>
          </span>
        ))}
        <input className="adm-tag-input" value={val} onChange={e => setVal(e.target.value)} onKeyDown={add} placeholder={placeholder} />
      </div>
    </Field>
  );
};

const MultiCheck = ({ label, options, selected, onChange, labelKey = 'name', valueKey = 'id' }) => (
  <Field label={label}>
    <div className="adm-multicheck">
      {options.map(opt => {
        const val = opt[valueKey];
        const checked = selected.includes(val);
        return (
          <label key={val} className="adm-check-label">
            <input
              type="checkbox"
              checked={checked}
              onChange={() => onChange(checked ? selected.filter(x => x !== val) : [...selected, val])}
            />
            {opt[labelKey]}
          </label>
        );
      })}
      {options.length === 0 && <span className="adm-muted" style={{ fontSize: 12 }}>None available</span>}
    </div>
  </Field>
);

const ImageUploadField = ({ label, value, onChange, bucket = 'product-images', required }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const url = await uploadImage(file, bucket);
      onChange(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Field label={label} required={required}>
      <div className="adm-img-field">
        <input
          className="adm-input"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="Paste URL or upload below"
        />
        <label className="adm-upload-btn">
          {uploading ? 'Uploading…' : '↑ Upload'}
          <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} disabled={uploading} />
        </label>
      </div>
      {error && <span className="adm-error">{error}</span>}
      {value && (
        <div className="adm-img-preview">
          <img src={value} alt="preview" onError={e => e.target.style.display = 'none'} />
        </div>
      )}
    </Field>
  );
};

const ProductCodeSearch = ({ products }) => {
  const [code, setCode]         = useState('');
  const [result, setResult]     = useState(null);
  const [notFound, setNotFound] = useState(false);

  const search = () => {
    if (!code.trim()) return;
    const found = products.find(
      p => p.product_code?.toLowerCase() === code.trim().toLowerCase()
    );
    setResult(found || null);
    setNotFound(!found);
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #e0d8cc', padding: '16px 20px', marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#B08D57' }}>
        Search by Product Code
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          className="adm-input"
          style={{ flex: 1, minWidth: 0 }}
          placeholder="Enter product code e.g. AAM-JHK-001"
          value={code}
          onChange={e => { setCode(e.target.value); setResult(null); setNotFound(false); }}
          onKeyDown={e => e.key === 'Enter' && search()}
        />
        <button className="adm-btn-primary" onClick={search} style={{ padding: '10px 20px', whiteSpace: 'nowrap', flexShrink: 0 }}>Search</button>
      </div>
      {notFound && (
        <div style={{ fontSize: 12, color: '#C0392B' }}>No product found with code "{code}"</div>
      )}
      {result && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 12, background: '#faf8f4', border: '1px solid #e0d8cc', flexWrap: 'wrap' }}>
          {(result.images?.[0] || result.image_1) && <img src={result.images?.[0] || result.image_1} alt={result.name} style={{ width: 56, height: 56, objectFit: 'cover', flexShrink: 0 }} />}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#1C1A18', marginBottom: 4 }}>{result.name}</div>
            <div style={{ fontSize: 11, color: '#8A7E72', letterSpacing: '0.06em' }}>
              Code: {result.product_code} · ₹{result.sale_price || result.original_price} · Stock:{' '}
              <span style={{ color: result.stock_qty === 0 ? '#C0392B' : '#2E7D32', fontWeight: 600 }}>
                {result.stock_qty === 0 ? 'Out of Stock' : result.stock_qty}
              </span>
            </div>
          </div>
          {result.badge && (
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', background: '#B08D57', color: '#fff', padding: '4px 10px', flexShrink: 0 }}>
              {result.badge}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

const ProductForm = ({ reels, collections, onSave, onCancel, initial }) => {
  const [form, setForm] = useState(() => {
    if (!initial) return EMPTY_PRODUCT;
    const existingImages = initial.images?.length
      ? initial.images
      : [initial.image_1, initial.image_2, initial.image_3].filter(Boolean);
    return { ...EMPTY_PRODUCT, ...initial, images: existingImages };
  });
  const [selectedCollections, setSC]  = useState(initial?.collections?.map(c => c.id) || []);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        original_price: parseFloat(form.original_price),
        sale_price:     form.sale_price ? parseFloat(form.sale_price) : null,
        reel_id:        form.reel_id || null,
        images:         (form.images || []).filter(url => url.trim() !== ''),
      };
      delete payload.collections;
      delete payload.product_collections;
      delete payload.image_1;
      delete payload.image_2;
      delete payload.image_3;
      let saved;
      if (initial?.id) {
        saved = await updateProduct(initial.id, payload, selectedCollections);
      } else {
        saved = await createProduct(payload, selectedCollections);
      }
      onSave(saved);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="adm-form" onSubmit={handleSubmit}>
      <div className="adm-form-grid">

        {/* Left column */}
        <div className="adm-col">
          <div className="adm-section-title">Basic Info</div>

          <Field label="Product Name" required>
            <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Antique Jhumka Set" required />
          </Field>

          <Field label="Description">
            <Textarea value={form.description} onChange={e => set('description', e.target.value)} placeholder="Describe the product…" />
          </Field>

          <Field label="Offer Text">
            <Input value={form.offer_text} onChange={e => set('offer_text', e.target.value)} placeholder="e.g. Buy 2 Get 1 Free" />
          </Field>

          <div className="adm-row-2">
            <Field label="Original Price (₹)" required>
              <Input type="number" value={form.original_price} onChange={e => set('original_price', e.target.value)} placeholder="1999" required />
            </Field>
            <Field label="Sale Price (₹)">
              <Input type="number" value={form.sale_price} onChange={e => set('sale_price', e.target.value)} placeholder="999" />
            </Field>
          </div>

          <div className="adm-row-2">
            <Field label="Badge">
              <Select value={form.badge} onChange={e => set('badge', e.target.value)}>
                <option value="">None</option>
                {BADGE_OPTIONS.map(b => <option key={b}>{b}</option>)}
              </Select>
            </Field>
            <Field label="Sort Order">
              <Input type="number" value={form.sort_order || 0} onChange={e => set('sort_order', parseInt(e.target.value))} />
            </Field>
          </div>

          <div className="adm-row-2">
            <Field label="Product Code (unique)">
              <Input
                value={form.product_code || ''}
                onChange={e => set('product_code', e.target.value)}
                placeholder="e.g. AAM-JHK-001"
              />
            </Field>
            <Field label="Stock Quantity">
              <Input
                type="number"
                value={form.stock_qty || 0}
                onChange={e => set('stock_qty', parseInt(e.target.value) || 0)}
                placeholder="0"
                min="0"
              />
            </Field>
          </div>

          <MultiCheck
            label="Collections (select one or more)"
            options={collections}
            selected={selectedCollections}
            onChange={setSC}
          />

          <Field label="Link to Reel (optional)">
            <Select value={form.reel_id} onChange={e => set('reel_id', e.target.value)}>
              <option value="">Not linked</option>
              {reels.map(r => <option key={r.id} value={r.id}>{r.title}</option>)}
            </Select>
          </Field>

          <TagInput label="Colors (Enter to add)" values={form.colors || []} onChange={v => set('colors', v)} placeholder="e.g. Gold, Silver…" />
          <TagInput label="Sizes (Enter to add)"  values={form.sizes  || []} onChange={v => set('sizes',  v)} placeholder="e.g. S, M, L…" />

          <div className="adm-checkboxes">
            <label className="adm-check-label">
              <input type="checkbox" checked={form.is_featured} onChange={e => set('is_featured', e.target.checked)} />
              Featured product
            </label>
            <label className="adm-check-label">
              <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} />
              Active (visible to users)
            </label>
          </div>
        </div>

        {/* Right column — images */}
        <div className="adm-col">
          <div className="adm-section-title">
            Product Images
            <span className="adm-muted" style={{ marginLeft: 8 }}>({(form.images || []).length} added)</span>
          </div>
          {(form.images || []).map((url, idx) => (
            <div key={idx} style={{ position: 'relative' }}>
              <ImageUploadField
                label={`Image ${idx + 1}${idx === 0 ? ' (main)' : ' (optional)'}`}
                value={url}
                onChange={newUrl => {
                  const updated = [...(form.images || [])];
                  updated[idx] = newUrl;
                  set('images', updated);
                }}
                bucket="product-images"
                required={idx === 0}
              />
              <button
                type="button"
                onClick={() => set('images', (form.images || []).filter((_, i) => i !== idx))}
                style={{
                  position: 'absolute', top: 0, right: 0,
                  background: '#fdecea', border: 'none', color: '#c0392b',
                  fontSize: 11, fontWeight: 700, padding: '4px 10px', cursor: 'pointer',
                  letterSpacing: '0.1em',
                }}
              >
                ✕ Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            className="adm-btn-ghost"
            style={{ marginTop: 4, width: '100%' }}
            onClick={() => set('images', [...(form.images || []), ''])}
          >
            + Add Image
          </button>
          {form.original_price && form.sale_price && (
            <div className="adm-sale-badge">
              {Math.round((1 - form.sale_price / form.original_price) * 100)}% OFF
            </div>
          )}
        </div>
      </div>

      {error && <div className="adm-form-error">{error}</div>}

      <div className="adm-form-actions">
        <button type="button" className="adm-btn-ghost" onClick={onCancel}>Cancel</button>
        <button type="submit" className="adm-btn-primary" disabled={saving}>
          {saving ? 'Saving…' : initial?.id ? 'Update Product' : 'Save Product'}
        </button>
      </div>
    </form>
  );
};

const VideoUploadField = ({ value, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError]        = useState('');

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      setError('File too large. Max 50MB allowed.'); return;
    }
    setUploading(true);
    setError('');
    try {
      const url = await uploadImage(file, 'reels-videos');
      onChange(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div className="adm-img-field">
        <input
          className="adm-input"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="Paste video URL or upload below"
        />
        <label className="adm-upload-btn">
          {uploading ? 'Uploading…' : '↑ Upload'}
          <input type="file" accept="video/*" style={{ display: 'none' }} onChange={handleFile} disabled={uploading} />
        </label>
      </div>
      {error && <span className="adm-error">{error}</span>}
      {value && (
        <video src={value} controls style={{ width: '100%', maxHeight: 180, background: '#1C1A18', marginTop: 4 }} />
      )}
    </div>
  );
};

const ReelForm = ({ onSave, onCancel, initial }) => {
  const [form, setForm] = useState(initial ? { ...EMPTY_REEL, ...initial } : EMPTY_REEL);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const saved = initial?.id
        ? await updateReel(initial.id, form)
        : await createReel(form);
      onSave(saved);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="adm-form adm-form--narrow" onSubmit={handleSubmit}>
      <div className="adm-section-title">{initial?.id ? 'Edit Reel' : 'New Reel'}</div>

      <Field label="Reel Title" required>
        <Input value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Bridal Kundan Sets" required />
      </Field>

      <Field label="Reel Video (max 50MB)">
        <VideoUploadField
          value={form.video_url || ''}
          onChange={url => set('video_url', url)}
        />
      </Field>

      <Field label="Handle">
        <Input value={form.handle} onChange={e => set('handle', e.target.value)} placeholder="@macra_era" />
      </Field>

      <label className="adm-check-label" style={{ marginTop: 8 }}>
        <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} />
        Active (visible to users)
      </label>

      {error && <div className="adm-form-error">{error}</div>}

      <div className="adm-form-actions">
        <button type="button" className="adm-btn-ghost" onClick={onCancel}>Cancel</button>
        <button type="submit" className="adm-btn-primary" disabled={saving}>
          {saving ? 'Saving…' : initial?.id ? 'Update Reel' : 'Save Reel'}
        </button>
      </div>
    </form>
  );
};

const CollectionForm = ({ onSave, onCancel, initial }) => {
  const [form, setForm] = useState(initial ? { ...EMPTY_COLLECTION, ...initial } : EMPTY_COLLECTION);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleName = (val) => {
    set('name', val);
    if (!initial?.id) {
      set('slug', val.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const saved = initial?.id
        ? await updateCollection(initial.id, form)
        : await createCollection(form);
      onSave(saved);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="adm-form adm-form--narrow" onSubmit={handleSubmit}>
      <div className="adm-section-title">{initial?.id ? 'Edit Collection' : 'New Collection'}</div>

      <Field label="Collection Name" required>
        <Input value={form.name} onChange={e => handleName(e.target.value)} placeholder="e.g. South Indian Traditional" required />
      </Field>

      <Field label="Slug" required>
        <Input value={form.slug} onChange={e => set('slug', e.target.value)} placeholder="south-indian-traditional" required />
      </Field>

      <Field label="Sub-title">
        <Input value={form.sub} onChange={e => set('sub', e.target.value)} placeholder="e.g. Temple jewellery & more" />
      </Field>

      <Field label="Badge">
        <Select value={form.badge} onChange={e => set('badge', e.target.value)}>
          <option value="">None</option>
          {BADGE_OPTIONS.map(b => <option key={b}>{b}</option>)}
        </Select>
      </Field>

      <ImageUploadField
        label="Collection Image"
        value={form.image_url}
        onChange={url => set('image_url', url)}
        bucket="product-images"
      />

      <div className="adm-row-2">
        <Field label="Sort Order">
          <Input type="number" value={form.sort_order} onChange={e => set('sort_order', parseInt(e.target.value))} />
        </Field>
      </div>

      <div className="adm-checkboxes" style={{ marginTop: 8 }}>
        <label className="adm-check-label">
          <input type="checkbox" checked={form.is_featured} onChange={e => set('is_featured', e.target.checked)} />
          Featured collection
        </label>
        <label className="adm-check-label">
          <input type="checkbox" checked={form.is_luxe} onChange={e => set('is_luxe', e.target.checked)} />
          Aamira Luxe
        </label>
      </div>

      {error && <div className="adm-form-error">{error}</div>}

      <div className="adm-form-actions">
        <button type="button" className="adm-btn-ghost" onClick={onCancel}>Cancel</button>
        <button type="submit" className="adm-btn-primary" disabled={saving}>
          {saving ? 'Saving…' : initial?.id ? 'Update Collection' : 'Save Collection'}
        </button>
      </div>
    </form>
  );
};

const ConfirmDialog = ({ message, onConfirm, onCancel }) => (
  <motion.div
    className="adm-modal-overlay"
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
  >
    <motion.div
      className="adm-modal"
      style={{ maxWidth: 400 }}
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
    >
      <div className="adm-modal-title" style={{ fontSize: 18 }}>Confirm Delete</div>
      <p style={{ fontSize: 13, color: '#3a3530', marginBottom: 24 }}>{message}</p>
      <div className="adm-form-actions" style={{ marginTop: 0, paddingTop: 0, border: 'none' }}>
        <button className="adm-btn-ghost" onClick={onCancel}>Cancel</button>
        <button className="adm-btn-primary" style={{ background: '#c0392b' }} onClick={onConfirm}>Delete</button>
      </div>
    </motion.div>
  </motion.div>
);

const ImageDimensionChecker = ({ url }) => {
  const [dims, setDims] = useState(null);

  useEffect(() => {
    if (!url) return;
    const img = new Image();
    img.onload = () => setDims({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => setDims(null);
    img.src = url;
  }, [url]);

  if (!dims) return null;

  const ratio      = dims.w / dims.h;
  const isPortrait = ratio < 1;
  const isTooNarrow= dims.w < 1200;
  const isIdeal    = ratio >= 2.5 && dims.w >= 1920;
  const isOk       = !isPortrait && !isTooNarrow;

  const color  = isIdeal ? '#2E7D32' : isOk ? '#B08D57' : '#C0392B';
  const bg     = isIdeal ? '#e8f5e9' : isOk ? '#fdf3e3' : '#fdecea';
  const border = isIdeal ? '#a5d6a7' : isOk ? '#f5d8a0' : '#ef9a9a';
  const msg    = isIdeal
    ? '✓ Perfect — ideal banner size'
    : isPortrait
    ? '⚠ Portrait image — will be heavily cropped top & bottom on the website'
    : isTooNarrow
    ? '⚠ Too narrow — will look blurry on desktop. Minimum 1200px wide'
    : '↗ Acceptable — slight crop may occur. Ideal is 1920 × 600 px';

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      padding: '8px 12px', marginTop: 6,
      background: bg, border: `1px solid ${border}`, fontSize: 12, flexWrap: 'wrap',
    }}>
      <span style={{ fontWeight: 700, color, fontFamily: 'monospace', fontSize: 13 }}>
        {dims.w} × {dims.h}px
      </span>
      <span style={{ color: '#3a3530' }}>{msg}</span>
    </div>
  );
};

const BannerDimBadge = ({ url }) => {
  const [dims, setDims] = useState(null);
  useEffect(() => {
    if (!url) return;
    const img = new Image();
    img.onload = () => setDims({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = url;
  }, [url]);
  if (!dims) return null;
  const ok = dims.w / dims.h >= 1.5 && dims.w >= 1200;
  return (
    <div style={{
      position: 'absolute', bottom: 8, right: 8,
      fontSize: 9, fontWeight: 700,
      background: ok ? 'rgba(39,174,96,0.85)' : 'rgba(192,57,43,0.85)',
      color: '#fff', padding: '2px 8px', letterSpacing: '0.06em',
    }}>
      {dims.w}×{dims.h}
    </div>
  );
};

const EMPTY_BANNER = { image_url: '', title: '', link_url: '', is_active: true, sort_order: 0 };

const BannerForm = ({ onSave, onCancel, initial, collections = [] }) => {
  const [form, setForm]     = useState(initial ? { ...EMPTY_BANNER, ...initial } : EMPTY_BANNER);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const decodedLink = form.link_url ? decodeURIComponent(form.link_url) : '';
  const selectedCollectionId = collections.find(c => decodedLink === `/collections/${c.slug}`)?.id || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const saved = initial?.id
        ? await updateBanner(initial.id, form)
        : await createBanner(form);
      onSave(saved);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="adm-form adm-form--narrow" onSubmit={handleSubmit}>
      <div className="adm-section-title">{initial?.id ? 'Edit Banner' : 'New Banner'}</div>

      <div style={{
        background: '#fdf9f4', border: '1px solid #e0d8cc',
        padding: '12px 14px', marginBottom: 16, fontSize: 12,
        color: '#5C4A2A', lineHeight: 1.7,
      }}>
        <div style={{
          fontWeight: 600, fontSize: 10, letterSpacing: '0.16em',
          textTransform: 'uppercase', color: '#B08D57', marginBottom: 8,
        }}>
          Offer banner container size
        </div>
        <div style={{ marginBottom: 4 }}>
          <strong>Required: 1920 × 600 px</strong> — this is the homepage offer banner container size.
        </div>
        <div style={{ marginBottom: 4 }}>
          Only upload banners meant for this offer banner slot. Images with other dimensions may appear zoomed, cropped, or misaligned.
        </div>
        <div style={{ marginBottom: 4 }}>
          Minimum width: <strong>1200 px</strong>. Narrower images look blurry on desktop.
        </div>
        <div>
          Keep important content centered and use landscape format for a clean fit.
        </div>
      </div>

      <ImageUploadField
        label="Banner Image"
        value={form.image_url}
        onChange={url => set('image_url', url)}
        bucket="site-banners"
        required
      />

      {form.image_url && <ImageDimensionChecker url={form.image_url} />}

      <Field label="Title (internal label — not shown on website)">
        <Input
          value={form.title}
          onChange={e => set('title', e.target.value)}
          placeholder="e.g. Diwali Sale Banner"
        />
      </Field>

      <Field label="Select Collection (optional)">
        <Select
          value={selectedCollectionId}
          onChange={e => {
            const slug = e.target.value;
            set('link_url', slug ? `/collections/${slug}` : '');
          }}
        >
          <option value="">Choose collection</option>
          {collections.map(c => (
            <option key={c.id} value={c.slug}>{c.name}</option>
          ))}
        </Select>
        <div style={{ fontSize: 12, color: '#6b6357', marginTop: 6 }}>
          Choosing a collection will auto-fill the banner link to that collection page.
        </div>
      </Field>

      <Field label="Sort Order (lower = shown first)">
        <Input
          type="number"
          value={form.sort_order}
          onChange={e => set('sort_order', parseInt(e.target.value) || 0)}
        />
      </Field>

      <label className="adm-check-label" style={{ marginTop: 8 }}>
        <input
          type="checkbox"
          checked={form.is_active}
          onChange={e => set('is_active', e.target.checked)}
        />
        Active (visible on website)
      </label>

      {error && <div className="adm-form-error">{error}</div>}

      <div className="adm-form-actions">
        <button type="button" className="adm-btn-ghost" onClick={onCancel}>Cancel</button>
        <button type="submit" className="adm-btn-primary" disabled={saving}>
          {saving ? 'Saving…' : initial?.id ? 'Update Banner' : 'Save Banner'}
        </button>
      </div>
    </form>
  );
};

// ══════════════════════════════════════════════════════════
//  MAIN ADMIN DASHBOARD
// ══════════════════════════════════════════════════════════
const AdminDashboard = () => {
  const navigate = useNavigate();

  const [tab, setTab]           = useState('products');
  const [products, setProducts] = useState([]);
  const [reels, setReels]       = useState([]);
  const [collections, setCols]  = useState([]);
  const [orders, setOrders]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [loadErr, setLoadErr]   = useState('');
  const [banners, setBanners]         = useState([]);
  const [bannerModal, setBannerModal] = useState(null);
  const [mobileTabOpen, setMobileTabOpen] = useState(false);

  const [productModal, setProductModal]     = useState(null);
  const [reelModal, setReelModal]           = useState(null);
  const [collectionModal, setColModal]      = useState(null);
  const [confirmDelete, setConfirmDelete]   = useState(null);
  const [trackingModal, setTrackingModal] = useState(null);
  const [trackingInput, setTrackingInput] = useState('');
  const [trackingError, setTrackingError] = useState('');
  const [trackingLoading, setTrackingLoading] = useState(false);

  const TABS = [
    { id: 'products',    label: 'Products' },
    { id: 'reels',       label: 'Reels' },
    { id: 'collections', label: 'Collections' },
    { id: 'orders',      label: 'Orders' },
    { id: 'banners',     label: 'Banners' },
  ];

  const stats = [
    { label: 'Total Products',  value: products.length },
    { label: 'Active Products', value: products.filter(p => p.is_active).length },
    { label: 'Total Orders',    value: orders.length },
    { label: 'Orders to Ship',  value: orders.filter(o => o.status === 'Paid').length },
    { label: 'Active Banners',  value: banners.filter(b => b.is_active).length },
  ];

  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadErr('');
    try {
      const [p, r, c, o, b] = await Promise.all([
        getAdminProducts(),
        getAdminReels(),
        getAdminCollections(),
        getAdminOrders(),
        getAdminBanners(),
      ]);
      setProducts(p);
      setReels(r);
      setCols(c);
      setOrders(o);
      setBanners(b);
    } catch (err) {
      setLoadErr(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = 'Admin Dashboard | Aamira';
    loadAll();
  }, [loadAll]);

  const handleLogout = async () => {
    try { await adminLogout(); } catch (_) {}
    navigate('/admin/login', { replace: true });
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return;
    const { type, id } = confirmDelete;
    try {
      if (type === 'product')    { await deleteProduct(id);    setProducts(p => p.filter(x => x.id !== id)); }
      if (type === 'reel')       { await deleteReel(id);       setReels(r => r.filter(x => x.id !== id)); }
      if (type === 'collection') { await deleteCollection(id); setCols(c => c.filter(x => x.id !== id)); }
      if (type === 'banner')     { await deleteBanner(id);     setBanners(b => b.filter(x => x.id !== id)); }
    } catch (err) {
      alert('Delete failed: ' + err.message);
    } finally {
      setConfirmDelete(null);
    }
  };

  const handleMarkShipped = async () => {
    if (!trackingInput.trim()) {
      setTrackingError('Please enter a tracking number');
      return;
    }
    setTrackingLoading(true);
    setTrackingError('');
    try {
      const updated = await updateOrderStatus(trackingModal.orderId, 'Shipped', trackingInput.trim());
      setOrders(prev => prev.map(x => x.id === trackingModal.orderId ? { ...x, ...updated } : x));
      setTrackingModal(null);
      setTrackingInput('');
    } catch (err) {
      setTrackingError(err.message);
    } finally {
      setTrackingLoading(false);
    }
  };

  const handleProductSaved = (saved) => {
    setProducts(prev => {
      const exists = prev.find(p => p.id === saved.id);
      if (exists) return prev.map(p => p.id === saved.id ? { ...p, ...saved } : p);
      return [saved, ...prev];
    });
    setProductModal(null);
    loadAll();
  };

  const handleReelSaved = (saved) => {
    setReels(prev => {
      const exists = prev.find(r => r.id === saved.id);
      if (exists) return prev.map(r => r.id === saved.id ? saved : r);
      return [saved, ...prev];
    });
    setReelModal(null);
  };

  const handleCollectionSaved = (saved) => {
    setCols(prev => {
      const exists = prev.find(c => c.id === saved.id);
      if (exists) return prev.map(c => c.id === saved.id ? saved : c);
      return [saved, ...prev];
    });
    setColModal(null);
  };

  const currentTabLabel = TABS.find(t => t.id === tab)?.label || 'Products';

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=Jost:wght@300;400;500;600&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .adm-root {
          min-height: 100vh;
          background: #F4F2EE;
          font-family: 'Jost', sans-serif;
          color: #1C1A18;
        }

        /* ── Top bar ── */
        .adm-topbar {
          background: #1C1A18;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 clamp(16px, 4vw, 48px);
          height: 56px;
          position: sticky;
          top: 0;
          z-index: 100;
          border-bottom: 1px solid rgba(176,141,87,0.2);
        }
        .adm-logo {
          font-family: 'Playfair Display', serif;
          font-size: clamp(14px, 4vw, 18px);
          font-weight: 700;
          color: #D4AF78;
          letter-spacing: 0.02em;
        }
        .adm-logo span {
          font-weight: 400;
          font-style: italic;
          color: rgba(212,175,120,0.6);
          font-size: clamp(11px, 2.5vw, 13px);
          margin-left: 8px;
        }

        /* ── Body ── */
        .adm-body {
          max-width: 1400px;
          margin: 0 auto;
          padding: 20px clamp(12px, 3vw, 48px) 80px;
        }

        /* ── Stats ── */
        .adm-stats {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 10px;
          margin-bottom: 24px;
        }
        .adm-stat { background: #fff; padding: clamp(14px, 3vw, 20px) clamp(12px, 2.5vw, 24px); border-left: 3px solid #B08D57; }
        .adm-stat-val {
          font-family: 'Playfair Display', serif;
          font-size: clamp(22px, 5vw, 32px); font-weight: 700; color: #1C1A18; line-height: 1;
        }
        .adm-stat-label {
          font-size: clamp(8px, 1.8vw, 10px); font-weight: 500; letter-spacing: 0.14em;
          text-transform: uppercase; color: #9A9186; margin-top: 4px;
        }

        /* ── Desktop Tabs ── */
        .adm-tabs {
          display: flex;
          border-bottom: 1px solid #e0d8cc;
          margin-bottom: 24px;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
        }
        .adm-tabs::-webkit-scrollbar { display: none; }
        .adm-tab {
          font-family: 'Jost', sans-serif;
          font-size: 11px; font-weight: 500; letter-spacing: 0.16em;
          text-transform: uppercase; background: none; border: none;
          padding: 14px clamp(12px, 2.5vw, 24px); cursor: pointer; color: #9A9186;
          border-bottom: 2px solid transparent; margin-bottom: -1px;
          transition: color 0.2s, border-color 0.2s;
          white-space: nowrap; flex-shrink: 0;
        }
        .adm-tab.active { color: #1C1A18; border-bottom-color: #B08D57; }
        .adm-tab:hover  { color: #B08D57; }

        /* ── Mobile tab dropdown ── */
        .adm-mobile-tab-select {
          display: none;
          width: 100%;
          font-family: 'Jost', sans-serif;
          font-size: 12px; font-weight: 600; letter-spacing: 0.14em;
          text-transform: uppercase; background: #fff; color: #1C1A18;
          border: 1px solid #e0d8cc; padding: 12px 14px;
          margin-bottom: 20px; outline: none;
          -webkit-appearance: none; appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23B08D57' d='M1 1l5 5 5-5'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 14px center;
          padding-right: 36px;
        }

        /* ── Section header ── */
        .adm-sec-header {
          display: flex; align-items: flex-start; justify-content: space-between;
          margin-bottom: 20px; flex-wrap: wrap; gap: 12px;
        }
        .adm-sec-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(18px, 4vw, 22px); font-weight: 700; color: #1C1A18;
        }
        .adm-sec-title em { font-style: italic; font-weight: 400; color: #B08D57; }

        /* ── Buttons ── */
        .adm-btn-primary {
          font-family: 'Jost', sans-serif;
          font-size: 10px; font-weight: 600; letter-spacing: 0.18em;
          text-transform: uppercase; background: #1C1A18; color: #fff;
          border: none; padding: 12px 20px; cursor: pointer; transition: background 0.2s;
          white-space: nowrap;
        }
        .adm-btn-primary:hover:not(:disabled) { background: #B08D57; }
        .adm-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

        .adm-btn-ghost {
          font-family: 'Jost', sans-serif;
          font-size: 10px; font-weight: 500; letter-spacing: 0.16em;
          text-transform: uppercase; background: transparent; color: #1C1A18;
          border: 1px solid #d4c5a9; padding: 11px 20px; cursor: pointer; transition: all 0.2s;
          white-space: nowrap;
        }
        .adm-btn-ghost:hover { border-color: #1C1A18; background: #1C1A18; color: #fff; }

        .adm-btn-logout {
          font-family: 'Jost', sans-serif;
          font-size: 10px; font-weight: 600; letter-spacing: 0.18em;
          text-transform: uppercase; background: transparent; color: #D4AF78;
          border: 1px solid rgba(212,175,120,0.6); padding: 8px 14px; cursor: pointer; transition: all 0.2s;
          white-space: nowrap;
        }
        .adm-btn-logout:hover { background: #B08D57; color: #fff; border-color: #B08D57; }

        .adm-btn-sm {
          font-size: 9px; font-weight: 600; letter-spacing: 0.14em;
          text-transform: uppercase; padding: 6px 12px; border: none; cursor: pointer; transition: background 0.2s;
          white-space: nowrap;
        }
        .adm-btn-edit   { background: #f0ebe3; color: #3a3530; }
        .adm-btn-edit:hover   { background: #B08D57; color: #fff; }
        .adm-btn-delete { background: #fdecea; color: #c0392b; }
        .adm-btn-delete:hover { background: #c0392b; color: #fff; }

        /* ── Modal ── */
        .adm-modal-overlay {
          position: fixed; inset: 0;
          background: rgba(28,26,24,0.55);
          z-index: 10000; display: flex;
          align-items: flex-start; justify-content: center;
          padding: clamp(12px, 3vw, 40px) clamp(12px, 3vw, 16px);
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
        }
        .adm-modal {
          background: #fff; width: 100%; max-width: 900px;
          z-index: 10001; padding: clamp(20px, 4vw, 36px); position: relative;
          margin-bottom: 40px;
        }
        .adm-modal-close {
          position: absolute; top: 12px; right: 16px;
          background: none; border: none; font-size: 22px; color: #9A9186; cursor: pointer;
          padding: 4px 8px; line-height: 1;
        }
        .adm-modal-close:hover { color: #1C1A18; }
        .adm-modal-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(18px, 4vw, 22px); font-weight: 700; color: #1C1A18;
          margin-bottom: 24px; padding-bottom: 16px; border-bottom: 1px solid #f0ebe3;
          padding-right: 32px;
        }
        .adm-modal-title em { font-style: italic; font-weight: 400; color: #B08D57; }

        /* ── Form ── */
        .adm-form { width: 100%; }
        .adm-form--narrow { max-width: 100%; }
        .adm-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 28px; }
        .adm-col { display: flex; flex-direction: column; gap: 16px; }
        .adm-row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .adm-section-title {
          font-size: 10px; font-weight: 600; letter-spacing: 0.26em;
          text-transform: uppercase; color: #B08D57;
          padding-bottom: 10px; border-bottom: 1px solid #f0ebe3; margin-bottom: 4px;
        }
        .adm-muted { color: #b0a99e; font-weight: 400; }

        .adm-field { display: flex; flex-direction: column; gap: 6px; }
        .adm-label {
          font-size: 10px; font-weight: 600; letter-spacing: 0.14em;
          text-transform: uppercase; color: #6b6560;
        }
        .adm-req { color: #C0392B; margin-left: 2px; }

        .adm-input {
          font-family: 'Jost', sans-serif; font-size: 14px; color: #1C1A18;
          background: #FAF8F4; border: 1px solid #e0d8cc;
          padding: 11px 14px; outline: none; transition: border-color 0.2s; width: 100%;
          -webkit-appearance: none; border-radius: 0;
        }
        .adm-input:focus { border-color: #B08D57; background: #fff; }

        .adm-textarea {
          font-family: 'Jost', sans-serif; font-size: 14px; color: #1C1A18;
          background: #FAF8F4; border: 1px solid #e0d8cc;
          padding: 11px 14px; outline: none; resize: vertical;
          transition: border-color 0.2s; width: 100%;
          -webkit-appearance: none; border-radius: 0;
        }
        .adm-textarea:focus { border-color: #B08D57; background: #fff; }

        /* ── Multi-check ── */
        .adm-multicheck {
          background: #FAF8F4; border: 1px solid #e0d8cc;
          padding: 10px 14px; display: flex; flex-wrap: wrap; gap: 8px;
          max-height: 160px; overflow-y: auto;
        }

        /* ── Tag input ── */
        .adm-tag-wrap {
          display: flex; flex-wrap: wrap; gap: 6px;
          background: #FAF8F4; border: 1px solid #e0d8cc;
          padding: 8px 10px; min-height: 46px; align-items: center; transition: border-color 0.2s;
        }
        .adm-tag-wrap:focus-within { border-color: #B08D57; background: #fff; }
        .adm-tag {
          display: inline-flex; align-items: center; gap: 5px;
          background: #1C1A18; color: #D4AF78;
          font-size: 10px; font-weight: 600; letter-spacing: 0.1em; padding: 3px 10px;
        }
        .adm-tag-x { background: none; border: none; color: rgba(212,175,120,0.6); cursor: pointer; font-size: 14px; line-height: 1; padding: 0; }
        .adm-tag-x:hover { color: #fff; }
        .adm-tag-input { border: none; background: transparent; outline: none; font-family: 'Jost', sans-serif; font-size: 13px; color: #1C1A18; min-width: 80px; flex: 1; }

        /* ── Checkboxes ── */
        .adm-checkboxes { display: flex; flex-direction: column; gap: 12px; margin-top: 4px; }
        .adm-check-label { display: flex; align-items: center; gap: 10px; font-size: 13px; color: #3a3530; cursor: pointer; }
        .adm-check-label input { accent-color: #B08D57; width: 16px; height: 16px; flex-shrink: 0; }

        /* ── Image field ── */
        .adm-img-field { display: flex; gap: 8px; }
        .adm-img-field .adm-input { flex: 1; min-width: 0; }
        .adm-upload-btn {
          font-family: 'Jost', sans-serif;
          font-size: 10px; font-weight: 600; letter-spacing: 0.14em;
          text-transform: uppercase; background: #f0ebe3; color: #3a3530;
          border: 1px solid #e0d8cc; padding: 0 14px; cursor: pointer;
          display: flex; align-items: center; white-space: nowrap; transition: all 0.2s;
          flex-shrink: 0;
        }
        .adm-upload-btn:hover { background: #B08D57; color: #fff; border-color: #B08D57; }

        .adm-img-preview { margin-top: 8px; width: 100%; height: 140px; background: #f0ebe3; overflow: hidden; }
        .adm-img-preview img { width: 100%; height: 100%; object-fit: cover; display: block; }

        .adm-sale-badge { background: #C0392B; color: #fff; font-size: 12px; font-weight: 700; letter-spacing: 0.1em; padding: 8px 18px; display: inline-block; margin-top: 8px; }

        .adm-form-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 28px; padding-top: 20px; border-top: 1px solid #f0ebe3; flex-wrap: wrap; }
        .adm-form-error { background: #fdecea; color: #c0392b; font-size: 12px; padding: 10px 14px; margin-top: 12px; }
        .adm-error { font-size: 11px; color: #c0392b; }

        /* ── Scrollable table wrapper ── */
        .adm-table-wrap { background: #fff; overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .adm-table { width: 100%; border-collapse: collapse; font-size: 12px; min-width: 600px; }
        .adm-table th {
          font-size: 9px; font-weight: 600; letter-spacing: 0.2em; text-transform: uppercase;
          color: #9A9186; padding: 12px 16px; text-align: left;
          border-bottom: 1px solid #f0ebe3; background: #fdf9f4; white-space: nowrap;
        }
        .adm-table td { padding: 12px 16px; border-bottom: 1px solid #f9f5f0; color: #3a3530; vertical-align: middle; }
        .adm-table tr:hover td { background: #fdf9f4; }
        .adm-table-img { width: 44px; height: 44px; object-fit: cover; display: block; background: #f0ebe3; flex-shrink: 0; }
        .adm-table-name { font-weight: 500; color: #1C1A18; font-size: 13px; }
        .adm-table-badge {
          display: inline-block; font-size: 8px; font-weight: 600; letter-spacing: 0.14em;
          text-transform: uppercase; background: #B08D57; color: #fff; padding: 3px 8px; margin: 2px 2px 0 0;
        }
        .adm-status-dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; margin-right: 6px; }
        .adm-status-dot.active   { background: #27ae60; }
        .adm-status-dot.inactive { background: #b0a99e; }
        .adm-table-actions { display: flex; gap: 6px; }

        /* ── Reel cards ── */
        .adm-reel-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 14px; }
        .adm-reel-card { background: #fff; overflow: hidden; }
        .adm-reel-cover { width: 100%; height: 240px; object-fit: cover; background: #f0ebe3; display: block; }
        .adm-reel-body { padding: 12px 14px 14px; border-top: 1px solid #f0ebe3; }
        .adm-reel-title { font-size: 12px; font-weight: 500; color: #1C1A18; margin-bottom: 4px; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
        .adm-reel-handle { font-size: 10px; color: #B08D57; letter-spacing: 0.08em; margin-bottom: 10px; }

        /* ── Collection cards ── */
        .adm-col-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 14px; }
        .adm-col-card {
          background: #fff;
          border: 1px solid #f0ebe3;
          border-radius: 10px;
          padding: 14px;
          display: flex;
          flex-direction: column;
          gap: 10px;
          box-shadow: 0 4px 12px rgba(28,26,24,0.05);
          transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .adm-col-card:hover {
          border-color: #B08D57;
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(28,26,24,0.1);
        }
        .adm-col-head {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 8px;
        }
        .adm-col-name { font-size: 14px; font-weight: 700; color: #1C1A18; line-height: 1.2; }
        .adm-col-sub  { font-size: 12px; color: #6f6962; margin-top: 2px; }
        .adm-col-badge {
          font-size: 9px; font-weight: 700; letter-spacing: 0.12em;
          text-transform: uppercase; background: #B08D57; color: '#fff';
          padding: 4px 8px; border-radius: 999px; white-space: nowrap; color: #fff;
        }
        .adm-col-actions {
          display: flex; flex-wrap: wrap; gap: 8px; margin-top: 4px;
        }

        /* ── Order cards ── */
        .adm-order-grid { display: flex; flex-direction: column; gap: 16px; }
        .adm-order-card {
          background: #fff; border: 1px solid #e0d8cc;
          padding: clamp(14px, 3vw, 20px); display: flex; flex-direction: column; gap: 14px;
        }
        .adm-order-2col {
          display: grid; grid-template-columns: 1fr 1fr; gap: 14px;
        }
        .adm-order-info-box {
          background: #faf8f4; padding: 12px 14px; border: 1px solid #f0ebe3;
        }
        .adm-order-info-label {
          font-size: 9px; font-weight: 600; letter-spacing: 0.2em;
          text-transform: uppercase; color: #B08D57; margin-bottom: 8px;
        }
        .adm-status-actions { display: flex; align-items: center; gap: 8px; padding-top: 8px; border-top: 1px solid #f0ebe3; flex-wrap: wrap; }

        /* ── Empty / loading states ── */
        .adm-empty { text-align: center; padding: 48px 20px; color: #9A9186; }
        .adm-empty-title { font-family: 'Playfair Display', serif; font-size: 20px; font-weight: 600; color: #3a3530; margin-bottom: 8px; }
        .adm-empty p { font-size: 13px; margin-bottom: 20px; }
        .adm-loading { text-align: center; padding: 60px; font-size: 13px; color: #9A9186; letter-spacing: 0.1em; }
        .adm-load-error { background: #fdecea; color: #c0392b; padding: 14px 18px; font-size: 13px; margin-bottom: 20px; }

        /* ════════════════════════
           RESPONSIVE BREAKPOINTS
           ════════════════════════ */

        /* Tablet: hide some table cols, adjust stats */
        @media (max-width: 900px) {
          .adm-stats { grid-template-columns: repeat(3, 1fr); }
          .adm-form-grid { grid-template-columns: 1fr; gap: 0; }
          .adm-col + .adm-col { margin-top: 24px; padding-top: 24px; border-top: 1px solid #f0ebe3; }
        }

        /* Mobile */
        @media (max-width: 640px) {
          .adm-body { padding: 16px 12px 80px; }
          .adm-stats { grid-template-columns: repeat(2, 1fr); gap: 8px; margin-bottom: 16px; }
          .adm-stats > div:last-child { grid-column: span 2; }

          /* Hide desktop tabs, show select */
          .adm-tabs { display: none; }
          .adm-mobile-tab-select { display: block; }

          /* Row-2 stacks on mobile */
          .adm-row-2 { grid-template-columns: 1fr; gap: 14px; }

          /* Modal full width, minimal padding */
          .adm-modal-overlay { padding: 0; align-items: flex-end; }
          .adm-modal {
            max-width: 100%; border-radius: 12px 12px 0 0;
            padding: 20px 16px 32px;
            max-height: 92vh; overflow-y: auto;
          }

          /* Form actions stack on mobile */
          .adm-form-actions { flex-direction: column-reverse; }
          .adm-form-actions button { width: 100%; text-align: center; padding: 14px; }
          .adm-btn-ghost { padding: 13px; }

          /* Order 2-col grid stacks */
          .adm-order-2col { grid-template-columns: 1fr; }

          /* Reel grid: 2 cols */
          .adm-reel-grid { grid-template-columns: repeat(2, 1fr); }
          .adm-reel-cover { height: 200px; }

          /* Collection grid: 1 col */
          .adm-col-grid { grid-template-columns: 1fr; }

          /* Status actions wrap nicely */
          .adm-status-actions { gap: 6px; }
          .adm-status-actions span { font-size: 9px; }

          /* Sticky add button area */
          .adm-sec-header { flex-direction: row; align-items: center; }

          /* Inputs bigger touch target */
          .adm-input, .adm-textarea { font-size: 16px; } /* prevent iOS zoom */
        }

        @media (max-width: 400px) {
          .adm-stats { grid-template-columns: 1fr 1fr; }
          .adm-reel-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="adm-root">

        {/* ── Top bar ── */}
        <div className="adm-topbar">
          <div className="adm-logo">Aamira <span>Admin</span></div>
          <button className="adm-btn-logout" onClick={handleLogout}>Exit</button>
        </div>

        <div className="adm-body">

          {/* ── Stats ── */}
          <div className="adm-stats">
            {stats.map((s, i) => (
              <motion.div key={s.label} className="adm-stat"
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.07 }}
              >
                <div className="adm-stat-val">{s.value}</div>
                <div className="adm-stat-label">{s.label}</div>
              </motion.div>
            ))}
          </div>

          {/* ── Load error ── */}
          {loadErr && (
            <div className="adm-load-error">
              Failed to load: {loadErr} —{' '}
              <button style={{ background: 'none', border: 'none', color: '#c0392b', cursor: 'pointer', textDecoration: 'underline' }} onClick={loadAll}>Retry</button>
            </div>
          )}

          {/* ── Desktop Tabs ── */}
          <div className="adm-tabs">
            {TABS.map(t => (
              <button key={t.id} className={`adm-tab${tab === t.id ? ' active' : ''}`} onClick={() => setTab(t.id)}>
                {t.label}
              </button>
            ))}
          </div>

          {/* ── Mobile Tab Select ── */}
          <select
            className="adm-mobile-tab-select"
            value={tab}
            onChange={e => setTab(e.target.value)}
          >
            {TABS.map(t => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>

          {loading ? (
            <div className="adm-loading">Loading…</div>
          ) : (
            <>
              {/* ════ PRODUCTS TAB ════ */}
              {tab === 'products' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <ProductCodeSearch products={products} />
                  <div className="adm-sec-header">
                    <h2 className="adm-sec-title">All <em>Products</em></h2>
                    <button className="adm-btn-primary" onClick={() => setProductModal('add')}>+ Add</button>
                  </div>

                  {products.length === 0 ? (
                    <div className="adm-empty">
                      <div className="adm-empty-title">No products yet</div>
                      <p>Add your first product to get started.</p>
                      <button className="adm-btn-primary" onClick={() => setProductModal('add')}>+ Add Product</button>
                    </div>
                  ) : (
                    <div className="adm-table-wrap">
                      <table className="adm-table">
                        <thead>
                          <tr>
                            <th>Image</th>
                            <th>Name</th>
                            <th>Collections</th>
                            <th>Price</th>
                            <th>Sale</th>
                            <th>Badge</th>
                            <th>Code</th>
                            <th>Stock</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {products.map(p => (
                            <tr key={p.id}>
                              <td>
                                {(p.images?.[0] || p.image_1)
                                  ? <img src={p.images?.[0] || p.image_1} alt={p.name} className="adm-table-img" />
                                  : <div className="adm-table-img" />}
                              </td>
                              <td><div className="adm-table-name">{p.name}</div></td>
                              <td>
                                {(p.collections || []).map(c => (
                                  <span key={c.id} className="adm-table-badge">{c.name}</span>
                                ))}
                              </td>
                              <td>₹{p.original_price}</td>
                              <td>{p.sale_price ? `₹${p.sale_price}` : '—'}</td>
                              <td>{p.badge && <span className="adm-table-badge">{p.badge}</span>}</td>
                              <td style={{ fontSize: 11, color: '#8A7E72', letterSpacing: '0.08em', fontFamily: 'monospace' }}>
                                {p.product_code || '—'}
                              </td>
                              <td>
                                <span style={{
                                  fontSize: 11, fontWeight: 600,
                                  color: p.stock_qty === 0 ? '#C0392B' : p.stock_qty <= 5 ? '#B08D57' : '#2E7D32'
                                }}>
                                  {p.stock_qty === 0 ? 'Out' : p.stock_qty}
                                </span>
                              </td>
                              <td>
                                <span className={`adm-status-dot ${p.is_active ? 'active' : 'inactive'}`} />
                                {p.is_active ? 'Active' : 'Hidden'}
                              </td>
                              <td>
                                <div className="adm-table-actions">
                                  <button className="adm-btn-sm adm-btn-edit" onClick={() => setProductModal(p)}>Edit</button>
                                  <button className="adm-btn-sm adm-btn-delete" onClick={() => setConfirmDelete({ type: 'product', id: p.id, name: p.name })}>Del</button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </motion.div>
              )}

              {/* ════ REELS TAB ════ */}
              {tab === 'reels' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <div className="adm-sec-header">
                    <h2 className="adm-sec-title">Shop by <em>Reel</em></h2>
                    <button className="adm-btn-primary" onClick={() => setReelModal('add')}>+ Add</button>
                  </div>

                  {reels.length === 0 ? (
                    <div className="adm-empty">
                      <div className="adm-empty-title">No reels yet</div>
                      <p>Create a reel to link products to your Instagram content.</p>
                      <button className="adm-btn-primary" onClick={() => setReelModal('add')}>+ Add Reel</button>
                    </div>
                  ) : (
                    <div className="adm-reel-grid">
                      {reels.map(r => (
                        <div key={r.id} className="adm-reel-card">
                          {r.video_url
                            ? <video src={r.video_url} muted loop autoPlay playsInline className="adm-reel-cover" style={{ objectFit: 'cover' }} />
                            : <div className="adm-reel-cover" style={{ background: '#1C1A18' }} />}
                          <div className="adm-reel-body">
                            <div className="adm-reel-title">{r.title}</div>
                            <div className="adm-reel-handle">{r.handle}</div>
                            <div className="adm-table-actions">
                              <button className="adm-btn-sm adm-btn-edit" onClick={() => setReelModal(r)}>Edit</button>
                              <button className="adm-btn-sm adm-btn-delete" onClick={() => setConfirmDelete({ type: 'reel', id: r.id, name: r.title })}>Delete</button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}

              {/* ════ ORDERS TAB ════ */}
              {tab === 'orders' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <div className="adm-sec-header">
                    <h2 className="adm-sec-title">All <em>Orders</em></h2>
                    <span style={{ fontSize: 11, color: '#8A7E72' }}>{orders.length} total</span>
                  </div>

                  {orders.length === 0 ? (
                    <div className="adm-empty">
                      <div className="adm-empty-title">No orders yet</div>
                      <p>Orders will appear here once customers place them.</p>
                    </div>
                  ) : (
                    <div className="adm-order-grid">
                      {orders.map(o => {
                        const addr    = o.addresses;
                        const user    = o.users;
                        const items   = Array.isArray(o.items) ? o.items : [];
                        const date    = new Date(o.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
                        const time    = new Date(o.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
                        const updDate = o.updated_at !== o.created_at
                          ? new Date(o.updated_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                          : null;
                        const updTime = o.updated_at !== o.created_at
                          ? new Date(o.updated_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
                          : null;

                        const statusColors = {
                          Pending:   { bg: '#fdf3e3', color: '#B08D57', border: '#f5d8a0' },
                          Paid:      { bg: '#e8f5e9', color: '#2E7D32', border: '#a5d6a7' },
                          Shipped:   { bg: '#e3f2fd', color: '#1565C0', border: '#90caf9' },
                          Delivered: { bg: '#e8f5e9', color: '#1B5E20', border: '#81c784' },
                          Cancelled: { bg: '#fdecea', color: '#C0392B', border: '#ef9a9a' },
                        };
                        const sc = statusColors[o.status] || statusColors['Pending'];

                        return (
                          <div key={o.id} className="adm-order-card">

                            {/* Order header */}
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={{ fontSize: 15, fontWeight: 700, color: '#1C1A18', fontFamily: 'Playfair Display, serif', marginBottom: 4 }}>
                                  #{o.order_number}
                                </div>
                                <div style={{ fontSize: 11, color: '#8A7E72' }}>
                                  {date} at {time}
                                </div>
                                {o.payment_intent_id && (
                                  <div style={{ fontSize: 10, color: '#8A7E72', marginTop: 3, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                    Order ID: {o.payment_intent_id}
                                  </div>
                                )}
                                {o.razorpay_payment_id && (
                                  <div style={{ fontSize: 10, color: '#2E7D32', marginTop: 3, fontFamily: 'monospace', fontWeight: 600, wordBreak: 'break-all' }}>
                                    Payment ID: {o.razorpay_payment_id}
                                  </div>
                                )}
                                <div style={{ marginTop: 4 }}>
                                  <span style={{
                                    padding: '2px 8px', borderRadius: 100,
                                    fontWeight: 700, fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase',
                                    background: ['Paid','Shipped','Delivered'].includes(o.status) ? '#e8f5e9' : '#fdf3e3',
                                    color: ['Paid','Shipped','Delivered'].includes(o.status) ? '#2E7D32' : '#B08D57',
                                  }}>
                                    {['Paid','Shipped','Delivered'].includes(o.status) ? '✓ Payment Received' : '⏳ Payment Pending'}
                                  </span>
                                </div>
                                {updDate && (
                                  <div style={{ fontSize: 11, color: '#8A7E72', marginTop: 3 }}>
                                    Updated: {updDate} at {updTime}
                                  </div>
                                )}
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                                <span style={{
                                  fontSize: 10, fontWeight: 700, letterSpacing: '0.14em',
                                  textTransform: 'uppercase', padding: '5px 12px',
                                  background: sc.bg, color: sc.color,
                                  border: `1px solid ${sc.border}`, borderRadius: 100,
                                }}>
                                  {o.status}
                                </span>
                                <span style={{ fontSize: 15, fontWeight: 700, color: '#B08D57', fontFamily: 'Playfair Display, serif' }}>
                                  ₹{Number(o.total).toLocaleString('en-IN')}
                                </span>
                              </div>
                            </div>

                            {/* User + Address */}
                            <div className="adm-order-2col">
                              <div className="adm-order-info-box">
                                <div className="adm-order-info-label">Customer</div>
                                <div style={{ fontSize: 13, fontWeight: 500, color: '#1C1A18', marginBottom: 3 }}>{user?.name || '—'}</div>
                                <div style={{ fontSize: 11, color: '#8A7E72' }}>{user?.email || '—'}</div>
                                <div style={{ fontSize: 11, color: '#8A7E72' }}>{user?.phone || addr?.phone || '—'}</div>
                              </div>
                              <div className="adm-order-info-box">
                                <div className="adm-order-info-label">Delivery Address</div>
                                {addr ? (
                                  <>
                                    <div style={{ fontSize: 13, fontWeight: 500, color: '#1C1A18', marginBottom: 3 }}>{addr.full_name}</div>
                                    <div style={{ fontSize: 11, color: '#8A7E72', lineHeight: 1.6 }}>
                                      {addr.line_1}{addr.line_2 ? `, ${addr.line_2}` : ''}<br />
                                      {addr.city}, {addr.state} — {addr.pincode}
                                    </div>
                                    <div style={{ fontSize: 11, color: '#8A7E72' }}>{addr.phone}</div>
                                  </>
                                ) : <div style={{ fontSize: 11, color: '#8A7E72' }}>No address</div>}
                              </div>
                            </div>

                            {/* Items */}
                            <div>
                              <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#B08D57', marginBottom: 10 }}>
                                Items ({items.length})
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {items.map((item, idx) => (
                                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f5f0e8' }}>
                                    {item.image && (
                                      <img src={item.image} alt={item.name} style={{ width: 44, height: 44, objectFit: 'cover', flexShrink: 0 }} />
                                    )}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ fontSize: 13, fontWeight: 500, color: '#1C1A18', marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                                      <div style={{ fontSize: 11, color: '#8A7E72', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        <span>Qty: {item.qty}</span>
                                        {item.size  && <span>Size: {item.size}</span>}
                                        {item.color && <span>Color: {item.color}</span>}
                                      </div>
                                    </div>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: '#B08D57', flexShrink: 0 }}>
                                      ₹{Number(item.price).toLocaleString('en-IN')}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Delivery + Value */}
                            <div className="adm-order-2col">
                              <div className="adm-order-info-box">
                                <div className="adm-order-info-label">Delivery Partner</div>
                                {o.delivery_partner ? (
                                  <>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1C1A18', marginBottom: 4 }}>
                                      {o.delivery_partner}
                                      <span style={{
                                        marginLeft: 8, fontSize: 9, fontWeight: 600, letterSpacing: '0.12em',
                                        textTransform: 'uppercase', padding: '2px 8px',
                                        background: o.delivery_type === 'Express' ? 'rgba(28,26,24,0.07)' : 'rgba(176,141,87,0.1)',
                                        color: o.delivery_type === 'Express' ? '#5C5247' : '#9A7A40',
                                      }}>
                                        {o.delivery_type}
                                      </span>
                                    </div>
                                    <div style={{ fontSize: 11, color: '#8A7E72', marginBottom: 3, textTransform: 'capitalize' }}>
                                      Zone: {o.delivery_zone === 'kerala' ? 'Kerala' : o.delivery_zone === 'south' ? 'South India' : 'North India'}
                                    </div>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: o.delivery_charge === 0 ? '#2E7D32' : '#B08D57' }}>
                                      Shipping: {o.delivery_charge === 0 ? 'Free' : `₹${Number(o.delivery_charge).toLocaleString('en-IN')}`}
                                    </div>
                                  </>
                                ) : (
                                  <div style={{ fontSize: 11, color: '#8A7E72' }}>Not specified</div>
                                )}
                              </div>

                              <div className="adm-order-info-box">
                                <div className="adm-order-info-label">Order Value</div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#8A7E72' }}>
                                    <span>Subtotal</span>
                                    <span>₹{(Number(o.total) + Number(o.voucher_discount || 0) - Number(o.delivery_charge || 0)).toLocaleString('en-IN')}</span>
                                  </div>
                                  {o.voucher_discount > 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#2E7D32', fontWeight: 600 }}>
                                      <span>Voucher Discount</span>
                                      <span>−₹{Number(o.voucher_discount).toLocaleString('en-IN')}</span>
                                    </div>
                                  )}
                                  {o.free_gift && <div style={{ fontSize: 11, color: '#9A7A40', fontWeight: 600 }}>🎁 Free Gift included</div>}
                                  {o.delivery_charge === 0 && o.delivery_partner && (
                                    <div style={{ fontSize: 11, color: '#2E7D32', fontWeight: 600 }}>🚚 Free Shipping</div>
                                  )}
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700, color: '#1C1A18', borderTop: '1px solid #f0ebe3', paddingTop: 6, marginTop: 2 }}>
                                    <span>Total Paid</span>
                                    <span style={{ color: '#B08D57', fontFamily: 'Playfair Display, serif' }}>₹{Number(o.total).toLocaleString('en-IN')}</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Status update */}
                            <div className="adm-status-actions">
                              <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#5C5247' }}>
                                Update:
                              </span>

                              <button
                                onClick={() => {
                                  setTrackingInput(o.tracking_number || '');
                                  setTrackingError('');
                                  setTrackingModal({ orderId: o.id, deliveryPartner: o.delivery_partner });
                                }}
                                style={{
                                  fontSize: 9, fontWeight: 600, letterSpacing: '0.14em',
                                  textTransform: 'uppercase', padding: '6px 12px',
                                  border: 'none', cursor: 'pointer', transition: 'all 0.2s',
                                  background: o.status === 'Shipped' ? statusColors['Shipped']?.bg : '#f0ebe3',
                                  color: o.status === 'Shipped' ? statusColors['Shipped']?.color : '#5C5247',
                                  outline: o.status === 'Shipped' ? `1.5px solid ${statusColors['Shipped']?.border}` : 'none',
                                }}
                              >
                                Shipped
                              </button>

                              {['Delivered', 'Cancelled'].map(s => {
                                const sColors = {
                                  Delivered: { bg: '#e8f5e9', color: '#1B5E20', border: '#81c784' },
                                  Cancelled: { bg: '#fdecea', color: '#C0392B', border: '#ef9a9a' },
                                };
                                return (
                                  <button
                                    key={s}
                                    onClick={async () => {
                                      try {
                                        const updated = await updateOrderStatus(o.id, s);
                                        setOrders(prev => prev.map(x => x.id === o.id ? { ...x, ...updated } : x));
                                      } catch (err) {
                                        alert('Failed to update: ' + err.message);
                                      }
                                    }}
                                    style={{
                                      fontSize: 9, fontWeight: 600, letterSpacing: '0.14em',
                                      textTransform: 'uppercase', padding: '6px 12px',
                                      border: 'none', cursor: 'pointer', transition: 'all 0.2s',
                                      background: o.status === s ? sColors[s]?.bg : '#f0ebe3',
                                      color: o.status === s ? sColors[s]?.color : '#5C5247',
                                      outline: o.status === s ? `1.5px solid ${sColors[s]?.border}` : 'none',
                                    }}
                                  >
                                    {s}
                                  </button>
                                );
                              })}

                              {o.tracking_number && (
                                <span style={{ fontSize: 10, color: '#8A7E72', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                  #{o.tracking_number}
                                </span>
                              )}
                            </div>

                          </div>
                        );
                      })}
                    </div>
                  )}
                </motion.div>
              )}

              {/* ════ COLLECTIONS TAB ════ */}
              {tab === 'collections' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <div className="adm-sec-header">
                    <h2 className="adm-sec-title">All <em>Collections</em></h2>
                    <button className="adm-btn-primary" onClick={() => setColModal('add')}>+ Add</button>
                  </div>

                  {collections.length === 0 ? (
                    <div className="adm-empty">
                      <div className="adm-empty-title">No collections yet</div>
                      <p>Add your first collection.</p>
                      <button className="adm-btn-primary" onClick={() => setColModal('add')}>+ Add Collection</button>
                    </div>
                  ) : (
                    <div className="adm-col-grid">
                      {collections.map((c, i) => (
                        <motion.div key={c.id} className="adm-col-card"
                          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: i * 0.03 }}
                        >
                          <div className="adm-col-head">
                            <div>
                              <div className="adm-col-name">{c.name}</div>
                              {c.sub && <div className="adm-col-sub">{c.sub}</div>}
                            </div>
                            {c.badge && <span className="adm-col-badge">{c.badge}</span>}
                          </div>
                          <div className="adm-col-actions">
                            <button className="adm-btn-sm adm-btn-edit" onClick={() => setColModal(c)}>Edit</button>
                            <button className="adm-btn-sm adm-btn-delete" onClick={() => setConfirmDelete({ type: 'collection', id: c.id, name: c.name })}>Delete</button>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}

              {/* ════ BANNERS TAB ════ */}
              {tab === 'banners' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <div className="adm-sec-header">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h2 className="adm-sec-title">Site <em>Banners</em></h2>
                      <div style={{ fontSize: 12, color: '#6b6357', marginTop: 4, maxWidth: 600 }}>
                        Use <strong>1920 × 600 px</strong> landscape images for best results.
                      </div>
                    </div>
                    <button className="adm-btn-primary" onClick={() => setBannerModal('add')}>+ Add</button>
                  </div>

                  {banners.length === 0 ? (
                    <div className="adm-empty">
                      <div className="adm-empty-title">No banners yet</div>
                      <p>Upload a banner image to display on your homepage.</p>
                      <button className="adm-btn-primary" onClick={() => setBannerModal('add')}>+ Add Banner</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      {banners.map((b, i) => (
                        <motion.div
                          key={b.id}
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: i * 0.05 }}
                          style={{ background: '#fff', border: '1px solid #e0d8cc' }}
                        >
                          <div style={{
                            width: '100%',
                            height: 'clamp(100px, 15vw, 200px)',
                            overflow: 'hidden',
                            position: 'relative',
                            background: '#f0ebe3',
                          }}>
                            <img
                              src={b.image_url}
                              alt={b.title || 'Banner'}
                              style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center center' }}
                            />
                            <div style={{
                              position: 'absolute', top: 8, left: 8,
                              fontSize: 9, fontWeight: 700, letterSpacing: '0.12em',
                              textTransform: 'uppercase', padding: '3px 8px',
                              background: b.is_active ? 'rgba(39,174,96,0.9)' : 'rgba(150,150,150,0.9)',
                              color: '#fff',
                            }}>
                              {b.is_active ? 'Active' : 'Hidden'}
                            </div>
                            <BannerDimBadge url={b.image_url} />
                          </div>

                          <div style={{
                            padding: '10px 14px',
                            display: 'flex', alignItems: 'center',
                            justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
                          }}>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div style={{ fontSize: 13, fontWeight: 500, color: '#1C1A18' }}>
                                {b.title || '(no title)'}
                              </div>
                              {b.link_url && (
                                <div style={{ fontSize: 11, color: '#B08D57', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {b.link_url}
                                </div>
                              )}
                              <div style={{ fontSize: 10, color: '#9A9186', marginTop: 2 }}>
                                Order: {b.sort_order}
                              </div>
                            </div>
                            <div className="adm-table-actions" style={{ flexShrink: 0 }}>
                              <button className="adm-btn-sm adm-btn-edit" onClick={() => setBannerModal(b)}>Edit</button>
                              <button className="adm-btn-sm adm-btn-delete" onClick={() => setConfirmDelete({ type: 'banner', id: b.id, name: b.title || 'this banner' })}>Delete</button>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}
            </>
          )}
        </div>

        {/* ════ PRODUCT MODAL ════ */}
        <AnimatePresence>
          {productModal && (
            <motion.div className="adm-modal-overlay"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={e => { if (e.target === e.currentTarget) setProductModal(null); }}
            >
              <motion.div className="adm-modal"
                initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 30 }}
              >
                <button className="adm-modal-close" onClick={() => setProductModal(null)}>×</button>
                <div className="adm-modal-title">
                  {productModal === 'add' ? <>Add New <em>Product</em></> : <>Edit <em>Product</em></>}
                </div>
                <ProductForm
                  reels={reels}
                  collections={collections}
                  initial={productModal !== 'add' ? productModal : null}
                  onSave={handleProductSaved}
                  onCancel={() => setProductModal(null)}
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ════ REEL MODAL ════ */}
        <AnimatePresence>
          {reelModal && (
            <motion.div className="adm-modal-overlay"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={e => { if (e.target === e.currentTarget) setReelModal(null); }}
            >
              <motion.div className="adm-modal" style={{ maxWidth: 560 }}
                initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 30 }}
              >
                <button className="adm-modal-close" onClick={() => setReelModal(null)}>×</button>
                <div className="adm-modal-title">
                  {reelModal === 'add' ? <>Add New <em>Reel</em></> : <>Edit <em>Reel</em></>}
                </div>
                <ReelForm
                  initial={reelModal !== 'add' ? reelModal : null}
                  onSave={handleReelSaved}
                  onCancel={() => setReelModal(null)}
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ════ COLLECTION MODAL ════ */}
        <AnimatePresence>
          {collectionModal && (
            <motion.div className="adm-modal-overlay"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={e => { if (e.target === e.currentTarget) setColModal(null); }}
            >
              <motion.div className="adm-modal" style={{ maxWidth: 560 }}
                initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 30 }}
              >
                <button className="adm-modal-close" onClick={() => setColModal(null)}>×</button>
                <div className="adm-modal-title">
                  {collectionModal === 'add' ? <>Add New <em>Collection</em></> : <>Edit <em>Collection</em></>}
                </div>
                <CollectionForm
                  initial={collectionModal !== 'add' ? collectionModal : null}
                  onSave={handleCollectionSaved}
                  onCancel={() => setColModal(null)}
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ════ BANNER MODAL ════ */}
        <AnimatePresence>
          {bannerModal && (
            <motion.div
              className="adm-modal-overlay"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={e => { if (e.target === e.currentTarget) setBannerModal(null); }}
            >
              <motion.div
                className="adm-modal" style={{ maxWidth: 560 }}
                initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 30 }}
              >
                <button className="adm-modal-close" onClick={() => setBannerModal(null)}>×</button>
                <div className="adm-modal-title">
                  {bannerModal === 'add' ? <>Add New <em>Banner</em></> : <>Edit <em>Banner</em></>}
                </div>
                <BannerForm
                  initial={bannerModal !== 'add' ? bannerModal : null}
                  collections={collections}
                  onSave={(saved) => {
                    setBanners(prev => {
                      const exists = prev.find(x => x.id === saved.id);
                      if (exists) return prev.map(x => x.id === saved.id ? saved : x);
                      return [saved, ...prev];
                    });
                    setBannerModal(null);
                  }}
                  onCancel={() => setBannerModal(null)}
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ════ TRACKING NUMBER MODAL ════ */}
        <AnimatePresence>
          {trackingModal && (
            <motion.div
              className="adm-modal-overlay"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={e => { if (e.target === e.currentTarget) setTrackingModal(null); }}
            >
              <motion.div
                className="adm-modal" style={{ maxWidth: 460 }}
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
              >
                <button className="adm-modal-close" onClick={() => setTrackingModal(null)}>×</button>
                <div className="adm-modal-title" style={{ fontSize: 18 }}>
                  Mark as <em>Shipped</em>
                </div>

                <div style={{ fontSize: 12, color: '#5C5247', marginBottom: 16, lineHeight: 1.6 }}>
                  Delivery partner: <strong>{trackingModal.deliveryPartner || 'Not specified'}</strong>
                </div>

                <div className="adm-field" style={{ marginBottom: 16 }}>
                  <label className="adm-label">
                    {trackingModal.deliveryPartner === 'India Post'
                      ? 'Consignment Number (e.g. EE123456789IN)'
                      : 'Tracking Number (e.g. B32123456)'}
                    <span className="adm-req">*</span>
                  </label>
                  <input
                    className="adm-input"
                    placeholder={trackingModal.deliveryPartner === 'India Post' ? 'EE123456789IN' : 'B32123456'}
                    value={trackingInput}
                    onChange={e => { setTrackingInput(e.target.value); setTrackingError(''); }}
                    onKeyDown={e => e.key === 'Enter' && !trackingLoading && handleMarkShipped()}
                    autoFocus
                  />
                  {trackingError && (
                    <div style={{ fontSize: 11, color: '#C0392B', marginTop: 6 }}>{trackingError}</div>
                  )}
                </div>

                <div className="adm-form-actions" style={{ marginTop: 0, paddingTop: 0, border: 'none' }}>
                  <button className="adm-btn-ghost" onClick={() => setTrackingModal(null)}>Cancel</button>
                  <button
                    className="adm-btn-primary"
                    disabled={trackingLoading || !trackingInput.trim()}
                    onClick={handleMarkShipped}
                  >
                    {trackingLoading ? 'Saving…' : 'Confirm Shipped'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ════ CONFIRM DELETE ════ */}
        <AnimatePresence>
          {confirmDelete && (
            <ConfirmDialog
              message={`Are you sure you want to delete "${confirmDelete.name}"? This cannot be undone.`}
              onConfirm={handleDeleteConfirm}
              onCancel={() => setConfirmDelete(null)}
            />
          )}
        </AnimatePresence>

      </div>
    </>
  );
};

export default AdminDashboard;